import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { ColumnHistory, lhRegistryApiRef } from './api';
import { Pill, Tone, fmtAt, shortActor } from './ui';

const show = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '∅';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
};
const ACTION: Record<string, [string, Tone]> = { add: ['추가', 'ok'], update: ['변경', 'info'], drop: ['삭제', 'neutral'] };

/** 컬럼 하나의 이력 (add/update/drop, 재추가 시 incarnation 이 여러 개) */
export const ColumnHistoryPanel = ({ tableId, columnName, onClose }: { tableId: number; columnName: string; onClose: () => void }) => {
  const api = useApi(lhRegistryApiRef);
  const [h, setH] = useState<ColumnHistory>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    api.columnHistory(tableId, columnName).then(r => !cancelled && setH(r)).catch(e => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [api, tableId, columnName]);

  return (
    <Box component="section" aria-label="컬럼 이력" border={1} borderColor="primary.light" borderRadius={8} bgcolor="background.default" px={1.75} py={1.5}>
      <Box display="flex" alignItems="center" style={{ gap: 8 }} mb={1}>
        <Typography variant="body2" style={{ flex: 1 }}>
          <b>컬럼 이력 · <span style={{ fontFamily: "'Roboto Mono', monospace" }}>{columnName}</span></b>{' '}
          <Typography component="span" variant="caption" color="textSecondary">
            — 같은 이름은 삭제 후 재추가돼도 한 줄기로 묶음{h && h.incarnations.length > 1 ? ` · 등록 ${h.incarnations.length}회` : ''}
          </Typography>
        </Typography>
        <Button size="small" variant="outlined" onClick={onClose}>닫기</Button>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {!h && !error && <CircularProgress size={20} />}
      {h && (
        <Box component="ol" m={0} p={0} display="flex" flexDirection="column" style={{ gap: 10, listStyle: 'none' }}>
          {/* 서버는 오래된 것부터 준다 — 최신이 위로 오게 뒤집는다 */}
          {[...h.events].reverse().map((e, i) => {
            const [label, tone] = ACTION[e.action] ?? [e.action, 'neutral' as Tone];
            return (
              <Box component="li" key={i} display="flex" style={{ gap: 10 }}>
                <Box flexShrink={0}><Pill tone={tone}>{label}</Pill></Box>
                <Box minWidth={0}>
                  <Typography variant="body2">
                    <b>{fmtAt(e.changedAt)}</b> · 테이블 v{e.tableVersion}{e.ticketKey ? ` · ${e.ticketKey}` : ''}{e.key ? ` · ${e.key}` : ''}
                  </Typography>
                  <Typography variant="caption" component="div" color="textSecondary">{shortActor(e.changedBy)} · “{e.reason}”</Typography>
                  <Typography variant="caption" component="div">
                    {e.action === 'update' ? `${show(e.old)} → ${show(e.new)}` : show(e.action === 'drop' ? e.old : e.new)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
